import type { FileAttachment } from '@forecastify/shared';
import type { SessionState } from '../session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ HANDING SOMEBODY A FILE, IN ONE PLACE. C22's code, extracted by C47. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `attachments.tsx` argues that a second list beside it *"would be a second place for the download,
 * the refusals and the remove to drift"*, and C47 needed a second list all the same: the Agent's
 * chips are a different shape from C22's rows — a filename and an icon above the message a file was
 * sent with, not a 36px thumbnail strip. So the SHAPE is duplicated and the MECHANISM is not, which
 * is the half of that warning that actually mattered.
 *
 * ⚠️⚠️ **THE BYTES COME THROUGH THE SESSION, NEVER FROM AN `<a href>`.** A plain link to the API
 * arrives unauthenticated — there is no cookie, the token is a bearer header — and putting it in a
 * query string would write it into every access log. So the request is made with the session's own
 * headers and the result handed to the browser as an object URL.
 *
 * ⚠️ **The URL is revoked immediately after the click**, which is safe because `click()` on a
 * synthetic anchor is synchronous: the browser has taken the blob by the time this returns. Leaving
 * it un-revoked pins the bytes in memory for the life of the tab.
 *
 * ⚠️ **A failure is silent here and that is the caller's decision to change.** Every caller today
 * sits next to a list that will show the file gone on its next read; a toast from inside a helper
 * would be a sentence chosen by the wrong layer (rule 7 puts the words in the catalogue, and the
 * catalogue is the screen's).
 */
export async function downloadAttachment(
  /* ⚠️ `Pick`, not the whole session: this needs one authenticated `fetch` and nothing else, and a
   * helper that takes the session's every field is one a test has to construct in full. */
  session: Pick<SessionState, 'request'>,
  attachment: Pick<FileAttachment, 'id' | 'filename'>,
): Promise<void> {
  const response = await session.request(`/files/${attachment.id}`).catch(() => null);
  if (response?.ok !== true) return;

  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = attachment.filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ HANDING SOMEBODY BYTES WE ALREADY HAVE. Extracted by C48, and the    │
 * │ reason is in this file's own header.                                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `downloadAttachment` above fetches the bytes; `saveCsv` in `exports/export-dialog.tsx` already had
 * them. Both then did the same five lines — blob, anchor, `download`, click, revoke — and C48 needed
 * a third caller for the Agent's reply. Three copies of a five-line mechanism is where one of them
 * quietly forgets to revoke, or sets `download` after the click.
 *
 * ⚠️⚠️ **NO BOM, AND THAT IS NOT AN OVERSIGHT — IT IS THE DIFFERENCE BETWEEN THE TWO CALLERS.** The
 * CSV export needs `\uFEFF` or Excel on Windows reads the file in the system codepage and every `ő`
 * arrives as mojibake, so `toCsv` writes one **on the server**, into the string. A `.md` file with a
 * BOM in it shows a stray `` in half the editors that open it. Whoever needs one adds it to the
 * content, where the format's own writer can argue for it; this function is about delivery.
 *
 * ⚠️ **The `charset` belongs in the MIME type the caller passes**, because a `Blob` says nothing about
 * encoding on its own and the browser will guess from the bytes when it re-reads the file.
 *
 * ⚠️ **Revoked immediately after the click**, which is safe for the reason the header gives: `click()`
 * on a synthetic anchor is synchronous.
 */
/**
 * ⚠️ **Exported since 15 Sep, for `file-route.tsx`.** It already existed as the middle of
 * `downloadAttachment` and of `saveCsv`; the file route has the bytes and the name from one
 * response and needs exactly this and nothing else.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function saveTextFile(filename: string, text: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * `1,4 MB` — the design system renders a size the way it renders every other figure.
 *
 * ⚠️ **Extracted from `attachments.tsx` by C47 for one reason: two lists showing the same file must
 * not disagree about how big it is.** A second copy rounding to two decimals where this one rounds
 * to one is the kind of difference nobody reports and everybody notices.
 */
export function readableFileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
